/**
 * 2 アプリが共通で使う Ant Design の部品。デモと同じ UI 基盤を使う。
 * 画面固有の組み合わせ（商品カードなど）はアプリ側に置く。
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";
import {
  Button as AntButton,
  Card as AntCard,
  Tag as AntTag,
  Typography,
  Flex,
  Progress,
} from "antd";

export function PageHeader({
  title,
  description,
  titleExtra,
  extra,
}: {
  title: ReactNode;
  description?: ReactNode;
  // titleExtra は見出しの直後に並べるもの（ヘルプの「?」）。extra は操作ボタン等で右端に置く。
  titleExtra?: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <Flex
      className="vd-page-header"
      justify="space-between"
      align="center"
      wrap
      gap={12}
      style={{ marginBottom: 16 }}
    >
      <div style={{ minWidth: 0 }}>
        <Flex align="center" gap={6}>
          <Typography.Title level={1} style={{ margin: 0, fontSize: 20 }}>
            {title}
          </Typography.Title>
          {titleExtra}
        </Flex>
        {description === undefined ? null : (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {description}
          </Typography.Text>
        )}
      </div>
      {extra}
    </Flex>
  );
}

export function Card({
  title,
  extra,
  note,
  children,
}: {
  title?: ReactNode;
  extra?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <AntCard
      className="vd-card"
      title={
        title === undefined ? undefined : (
          <span className="vd-card-title">{title}</span>
        )
      }
      extra={extra}
    >
      {children}
      {note === undefined ? null : (
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 0, marginTop: 12, fontSize: 12 }}
        >
          {note}
        </Typography.Paragraph>
      )}
    </AntCard>
  );
}

export type TagTone = "neutral" | "ok" | "warn" | "danger" | "info";

export function Tag({
  tone = "neutral",
  children,
}: {
  tone?: TagTone;
  children: ReactNode;
}) {
  const colors = {
    neutral: undefined,
    ok: "success",
    warn: "warning",
    danger: "error",
    info: "processing",
  };
  return <AntTag color={colors[tone]}>{children}</AntTag>;
}

export function Button({
  variant = "default",
  size = "default",
  className,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> & {
  variant?: "default" | "primary" | "text";
  size?: "default" | "large";
}) {
  const { type = "button", ...props } = rest;
  return (
    <AntButton
      autoInsertSpace={false}
      htmlType={type}
      type={variant}
      size={size === "default" ? "middle" : size}
      className={className}
      {...props}
    />
  );
}

/** 数字 1 つ。母数の注記を数字と同じ塊に入れて、切り離せない形にする */
export function Stat({
  label,
  value,
  note,
  hero = false,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  hero?: boolean;
}) {
  return (
    <div>
      <div className="vd-stat-label">{label}</div>
      <div className={hero ? "vd-stat-hero" : "vd-stat-value"}>{value}</div>
      {note === undefined ? null : <div className="vd-stat-note">{note}</div>}
    </div>
  );
}

export function Meter({ ratio }: { ratio: number | null }) {
  const percent =
    ratio === null ? 0 : Math.max(0, Math.min(100, Math.round(ratio * 100)));
  return (
    <div
      className="vd-progress"
      role="meter"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <Progress
        percent={percent}
        showInfo={false}
        strokeColor="#7CC7B7"
        size="small"
      />
    </div>
  );
}

/**
 * 初期表示では畳んでおく注記。
 * 「未接続だから数えられていない」の説明は、開いたときだけ読ませる。
 */
export function Disclosure({
  summary,
  children,
}: {
  summary: ReactNode;
  children: ReactNode;
}) {
  return (
    <details className="vd-disclosure">
      <summary>{summary}</summary>
      <div className="vd-disclosure-body">{children}</div>
    </details>
  );
}

export function LoadingState({
  label = "読み込んでいます",
}: {
  label?: string;
}) {
  return (
    <div className="vd-state" role="status">
      <span className="vd-state-title">{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="vd-state">
      <span className="vd-state-title">{title}</span>
      {description === undefined ? null : <span>{description}</span>}
      {action}
    </div>
  );
}

export function ErrorState({
  title = "読み込めませんでした",
  description,
  onRetry,
  retryLabel = "もう一度読み込む",
}: {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
  /** やり直しが「読み込み直し」でないときに差し替える */
  retryLabel?: string;
}) {
  return (
    <div className="vd-state vd-state--error" role="alert">
      <span className="vd-state-title">{title}</span>
      {description === undefined ? null : <span>{description}</span>}
      {onRetry === undefined ? null : (
        <Button onClick={onRetry}>{retryLabel}</Button>
      )}
    </div>
  );
}
